import { SettingOutlined } from "@ant-design/icons";
import { Button, Checkbox } from "antd";
import { Drawer } from "components/UI/Drawer";
import { DrawerLink } from "components/UI/DrawerLink";
import { tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { useTable } from "components/UI/Table/hooks/useTable.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { DrawerId, Identifiable, TableId } from "types/models.ts";

import { TableColumnType } from "../../types/table";

import type { CheckboxOptionType } from "antd";

interface ChooseColumnsProps {
    tableId: TableId;
}

export const ColumnVisibility = <T extends Identifiable>({ tableId }: ChooseColumnsProps) => {
    const tableUtils = useTable(tableId);
    const [options, setOptions] = useState<CheckboxOptionType[]>([]);
    const [checkedList, setCheckedList] = useState<(string | string[] | number)[]>([]);
    const tableAtomInstance = tableAtom<T>();
    const [tableState, setTableState] = useRecoilState(tableAtomInstance(tableId));
    const { t } = usePaldockTranslation("tables.ui.column-visibility");

    useEffect(() => {
        const config = tableState.config;
        if (config) {
            setCheckedList(config.columnsConfig.filter((column) => column.visible).map((column) => column.dataIndex));
        }
    }, [tableState.config]);

    useEffect(() => {
        if (tableState.originColumns) {
            setOptions(
                tableState.originColumns.map(({ title, dataIndex, key }) => ({
                    // title can be ReactNode or function - use ReactNode directly, else fallback to key or dataIndex
                    label: typeof title === "function"
                        ? String(key || dataIndex || "Column")
                        : title,
                    value: dataIndex,
                }))
            );
        }
    }, [tableState.originColumns]);

    const handleCheckboxChange = (checkedValues: (string | number | string[])[]) => {
        const config = tableState.config;
        if (config) {
            const updatedConfig = {
                ...config,
                columnsConfig: config.columnsConfig.map((column) => ({
                    ...column,
                    visible: checkedValues.includes(column.dataIndex),
                })) as TableColumnType<Identifiable>[],
            };

            tableUtils.saveConfig(updatedConfig);
        }
    };

    return (
        <>
            <DrawerLink drawerId={DrawerId.TableColumnsVisibility} action={"show"}>
                <Button
                    type={"link"}
                    onClick={() =>
                        setTableState((prev) => {
                            return { ...prev, showColumnsVisibility: !prev.showColumnsVisibility };
                        })
                    }
                    size={"small"}
                    className="!text-black"
                >
                    <SettingOutlined />
                </Button>
            </DrawerLink>

            {tableState.showColumnsVisibility && (
                <Drawer drawerId={DrawerId.TableColumnsVisibility} title={t("title")}>
                    <Checkbox.Group
                        className={"flex flex-col gap-2"}
                        value={checkedList}
                        options={options as CheckboxOptionType[]}
                        onChange={handleCheckboxChange}
                    />
                </Drawer>
            )}
        </>
    );
};
